import { Link, useLocation } from "wouter";
import { useEffect } from "react";
import { useGetMe, useGetMailbox, useGetSalesforceStatus } from "@workspace/api-client-react";
import { UserButton as ClerkUserButton, useClerk as useClerkOriginal } from "@clerk/react";

const authBypass = import.meta.env.VITE_AUTH_BYPASS === "true";

function useClerkSafe() {
  if (authBypass) {
    return { signOut: () => {} } as { signOut: () => void };
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useClerkOriginal();
}

function UserButton() {
  if (authBypass) {
    return (
      <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">
        DA
      </div>
    );
  }
  return <ClerkUserButton />;
}
import { 
  Briefcase, 
  Building2,
  LayoutDashboard, 
  Settings, 
  FileText, 
  Send,
  LogOut,
  Menu,
  LifeBuoy,
  AlertCircle,
  Flame,
  BarChart2,
  ClipboardList,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useState } from "react";

const logoUrl = `${import.meta.env.BASE_URL}logo.svg`;
const whiteLogoStyle = { filter: "brightness(0) invert(1)" } as const;

function NavItems() {
  const [location] = useLocation();
  const [searchParams] = typeof window !== "undefined"
    ? [new URLSearchParams(window.location.search)]
    : [new URLSearchParams()];
  const { data: me } = useGetMe();

  const isDealsStaleActive =
    location === "/deals" && searchParams.get("stale") === "red";
  const isDealsActive =
    (location === "/deals" || location.startsWith("/deals/")) &&
    !isDealsStaleActive;

  const links = [
    { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    ...(me?.isAdmin ? [{ href: "/templates", label: "Templates", icon: FileText }] : []),
    { href: "/registrations", label: "Registrations", icon: Send },
    { href: "/suppliers", label: "Suppliers", icon: Building2 },
    ...(me?.isAdmin ? [{ href: "/reports", label: "Reports", icon: BarChart2 }] : []),
    { href: "/support", label: "Support", icon: LifeBuoy },
    ...(me?.isAdmin ? [{ href: "/support/admin", label: "Support Tickets", icon: FileText }] : []),
    ...(me?.isAdmin ? [{ href: "/admin/audit-log", label: "Audit Log", icon: ClipboardList }] : []),
    { href: "/settings", label: "Settings", icon: Settings },
  ];

  return (
    <nav className="flex flex-col gap-1 p-4">
      {/* Dashboard */}
      <Link href="/dashboard">
        <div className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors cursor-pointer ${
          location === "/dashboard"
            ? "bg-sidebar-primary/15 text-sidebar-primary font-medium"
            : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        }`}>
          <LayoutDashboard className="h-4 w-4" />
          <span>Dashboard</span>
        </div>
      </Link>

      {/* Deals group */}
      <Link href="/deals">
        <div className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors cursor-pointer ${
          isDealsActive
            ? "bg-sidebar-primary/15 text-sidebar-primary font-medium"
            : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        }`}>
          <Briefcase className="h-4 w-4" />
          <span>Deals</span>
        </div>
      </Link>
      {/* Stale registrations — pinned sub-view */}
      <Link href="/deals?stale=red">
        <div className={`flex items-center gap-3 pl-8 pr-3 py-1.5 rounded-md transition-colors cursor-pointer ${
          isDealsStaleActive
            ? "bg-red-500/15 text-red-600 dark:text-red-400 font-medium"
            : "text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        }`}>
          <Flame className="h-3.5 w-3.5 text-red-500" />
          <span className="text-sm">Stale registrations</span>
        </div>
      </Link>

      {links.filter(l => l.href !== "/dashboard").map((link) => {
        const isActive = location === link.href || location.startsWith(`${link.href}/`);
        const slug = link.href.replace(/^\/+/, "").replace(/\//g, "-");
        return (
          <Link key={link.href} href={link.href} data-testid={`nav-link-${slug}`}>
            <div className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors cursor-pointer ${
              isActive
                ? "bg-sidebar-primary/15 text-sidebar-primary font-medium"
                : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            }`}>
              <link.icon className="h-4 w-4" />
              <span>{link.label}</span>
            </div>
          </Link>
        );
      })}
    </nav>
  );
}

function SalesforceNotConfiguredBanner() {
  const { data: me } = useGetMe();
  const { data: sfStatus } = useGetSalesforceStatus();
  const [location] = useLocation();

  if (!me?.isAdmin) return null;
  if (!sfStatus) return null;
  if (location === "/settings") return null;

  const hasAuthError = Boolean(sfStatus.lastAuthError);
  if (sfStatus.connected && !hasAuthError) return null;

  const message = hasAuthError
    ? "Salesforce authentication is failing. Edit credentials to reconnect."
    : "Salesforce is not connected. Import, sync, and supplier directory are disabled.";
  const buttonLabel = hasAuthError ? "Fix credentials" : "Connect Salesforce";

  return (
    <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 px-4 py-2 flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>{message}</span>
      </div>
      <Link href="/settings">
        <Button size="sm" variant="outline" className="shrink-0 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 h-7 text-xs">
          {buttonLabel}
        </Button>
      </Link>
    </div>
  );
}

export function AppLayout({ children }: { children: React.ReactNode }) {
  const { signOut } = useClerkSafe();
  const { data: me } = useGetMe();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [location] = useLocation();

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location]);

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row">
      {/* Mobile Nav Header */}
      <header className="md:hidden flex items-center justify-between p-4 border-b border-sidebar-border bg-sidebar text-sidebar-foreground">
        <div className="flex items-center gap-2 font-semibold text-sm text-sidebar-foreground">
          <img src={logoUrl} alt="TDM" style={whiteLogoStyle} className="h-7 w-auto shrink-0" />
          <span>Registration App</span>
        </div>
        <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-sidebar-foreground hover:bg-sidebar-accent">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0 bg-sidebar text-sidebar-foreground border-sidebar-border">
            <div className="p-4 border-b border-sidebar-border">
              <div className="flex items-center gap-2 font-semibold text-sm text-sidebar-foreground">
                <img src={logoUrl} alt="TDM" style={whiteLogoStyle} className="h-7 w-auto shrink-0" />
                <span>Registration App</span>
              </div>
            </div>
            <NavItems />
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-sidebar-border bg-sidebar-accent/40">
              <div className="flex items-center gap-3 mb-4">
                <UserButton />
                <div className="text-sm font-medium truncate text-sidebar-foreground">{me?.name || me?.email}</div>
              </div>
              <Button variant="outline" className="w-full justify-start border-sidebar-border bg-transparent text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground" onClick={() => signOut()}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </header>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-sidebar-border bg-sidebar text-sidebar-foreground h-screen sticky top-0">
        <div className="p-6 border-b border-sidebar-border">
          <Link href="/">
            <div className="flex items-center gap-2 font-bold text-base text-sidebar-foreground cursor-pointer">
              <img src={logoUrl} alt="TDM" style={whiteLogoStyle} className="h-8 w-auto shrink-0" />
              <span>Registration App</span>
            </div>
          </Link>
        </div>
        
        <div className="flex-1 overflow-auto">
          <NavItems />
        </div>

        <div className="p-4 border-t border-sidebar-border bg-sidebar-accent/30">
          <div className="flex items-center gap-3 mb-4 px-2">
            <UserButton />
            <div className="flex flex-col overflow-hidden">
              <span className="text-sm font-medium truncate text-sidebar-foreground">{me?.name || 'User'}</span>
              <span className="text-xs text-sidebar-foreground/60 truncate">{me?.email}</span>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground" onClick={() => signOut()}>
            <LogOut className="h-4 w-4 mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <SalesforceNotConfiguredBanner />
        <div className="flex-1 overflow-auto p-4 md:p-8">
          <div className="max-w-6xl mx-auto w-full">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}
